/**
 * Where a new account came from, sent once with the registration request.
 *
 * The API side, and why it exists at all, is `bozorbot.auth.attribution`. The
 * short version: this app carries no analytics tag because it shows customer
 * transcripts, so the registration itself is the only place a signup can be
 * tied to the channel that produced it.
 *
 * Everything here is best-effort. A value that is missing, odd or unreadable
 * is left out; the API drops anything else it does not like. Neither side may
 * ever make registration fail over it.
 */

/** In the order the form shows them. Values match the API's `HeardFrom`. */
export const HEARD_FROM = [
  { value: "search", label: "Search engine (Google, Yandex…)" },
  { value: "wordpress_org", label: "WordPress plugin directory" },
  { value: "ai_assistant", label: "AI assistant (ChatGPT, Claude…)" },
  { value: "social", label: "Social media" },
  { value: "community", label: "Forum or community" },
  { value: "recommendation", label: "Someone recommended it" },
  { value: "advert", label: "An advert" },
  { value: "other", label: "Somewhere else" },
] as const;

export type HeardFrom = (typeof HEARD_FROM)[number]["value"];

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

export interface SignupAttribution {
  heard_from?: HeardFrom;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer?: string;
  cta?: string;
  landing_language?: string;
}

function hostOf(url: string): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, "") || null;
  } catch {
    return null;
  }
}

/**
 * Read the attribution a visit arrived with.
 *
 * `ref` wins over the browser's referrer when both exist. A visitor who came
 * through the landing page has `bozor-bot.com` as their referrer here, which
 * says nothing; the landing page kept the referrer it saw first and hands it
 * over in `ref`, and that is the one that names the channel.
 */
export function attributionFrom(
  search: string,
  referrer: string,
  ownHost: string
): SignupAttribution {
  const params = new URLSearchParams(search);
  const found: SignupAttribution = {};

  const take = (name: string) => params.get(name)?.trim() || undefined;

  for (const key of UTM_KEYS) {
    const value = take(key);
    if (value) found[key] = value;
  }

  const handed = take("ref");
  const seen = hostOf(referrer);
  if (handed) found.referrer = handed;
  else if (seen && seen !== ownHost.replace(/^www\./, "")) found.referrer = seen;

  const cta = take("cta");
  if (cta) found.cta = cta;
  const language = take("lang");
  if (language) found.landing_language = language;

  return found;
}

const STORAGE_KEY = "bozorbot:attribution";

/**
 * The first attribution this tab saw, kept for the rest of the visit.
 *
 * First rather than latest, because the link that brought someone here is the
 * channel; a later visit to `/register` from the sign-in page carries no tags
 * and would otherwise overwrite it with nothing. Session storage rather than
 * local, so it ends with the visit instead of crediting a channel for a signup
 * weeks later. Storage can be unavailable (private windows, blocked site
 * data); then this is simply the current URL's attribution.
 */
export function captureAttribution(): SignupAttribution {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored) as SignupAttribution;
  } catch {
    // Unreadable or unparsable: fall through and read the URL instead.
  }

  const current = attributionFrom(
    window.location.search,
    document.referrer,
    window.location.hostname
  );

  try {
    if (Object.keys(current).length > 0) {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(current));
    }
  } catch {
    // Not being able to remember it costs nothing but this one visit.
  }

  return current;
}
